import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { useOtpRequest, useOtpVerify } from '../../hooks/use-auth';

const step1Schema = z.object({
  email: z.string().email('Email invalid'),
  clientCode: z.string().min(1, 'Codul clientului este obligatoriu'),
});

const step2Schema = z.object({
  code: z.string().length(6, 'Codul trebuie să aibă 6 cifre'),
});

export function ClientLoginForm() {
  const [step, setStep] = useState<1 | 2>(1);
  const [email, setEmail] = useState('');
  const navigate = useNavigate();
  const otpRequest = useOtpRequest();
  const otpVerify = useOtpVerify();

  const form1 = useForm<z.infer<typeof step1Schema>>({ resolver: zodResolver(step1Schema) });
  const form2 = useForm<z.infer<typeof step2Schema>>({ resolver: zodResolver(step2Schema) });

  const onStep1 = form1.handleSubmit(async (data) => {
    try {
      await otpRequest.mutateAsync(data);
      setEmail(data.email);
      form2.reset({ code: '' });
      setStep(2);
    } catch (err) {
      form1.setError('root', { message: (err as Error).message });
    }
  });

  const onStep2 = form2.handleSubmit(async (data) => {
    try {
      await otpVerify.mutateAsync({ email, code: data.code });
      navigate('/');
    } catch (err) {
      form2.setError('root', { message: (err as Error).message });
    }
  });

  if (step === 2) {
    return (
      <form onSubmit={onStep2} className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-dark-muted">
          Am trimis un cod la <strong>{email}</strong>
        </p>
        <Input
          {...form2.register('code')}
          label="Cod OTP"
          placeholder="000000"
          maxLength={6}
          autoComplete="one-time-code"
          error={form2.formState.errors.code?.message as string | undefined}
        />
        {form2.formState.errors.root && (
          <p className="text-sm text-error">{form2.formState.errors.root.message}</p>
        )}
        <Button type="submit" className="w-full" disabled={otpVerify.isPending}>
          {otpVerify.isPending ? 'Se verifică...' : 'Verifică codul'}
        </Button>
        <button type="button" onClick={() => setStep(1)} className="text-sm text-primary hover:underline dark:text-primary-light">
          Înapoi
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={onStep1} className="space-y-4">
      <Input
        {...form1.register('email')}
        label="Email"
        type="email"
        placeholder="nume@companie.ro"
        error={form1.formState.errors.email?.message as string | undefined}
      />
      <Input
        {...form1.register('clientCode')}
        label="Cod client"
        placeholder="ENC-001"
        error={form1.formState.errors.clientCode?.message as string | undefined}
      />
      {form1.formState.errors.root && (
        <p className="text-sm text-error">{form1.formState.errors.root.message}</p>
      )}
      <Button type="submit" className="w-full" disabled={otpRequest.isPending}>
        {otpRequest.isPending ? 'Se trimite...' : 'Trimite codul'}
      </Button>
    </form>
  );
}
